<template>
  <drag-and-drop enabled @drop-files="loadFiles" id="app-container">
    <template v-slot="{ dragHover }">
      <v-app class="pycad-app">
        <v-main id="content-main">
          <div class="pycad-shell">
            <pycad-top-toolbar />
            <div class="pycad-main-row">
              <div class="pycad-viewport-column">
                <VtkRenderWindowParent>
                  <layout-grid
                    v-if="hasData && !curvedMPRWorkspaceActive"
                    :layout="layout"
                  />
                  <component
                    :is="implantPlanningWorkspace?.workspaceComponent"
                    v-if="
                      hasData &&
                      curvedMPRWorkspaceActive &&
                      implantPlanningWorkspace
                    "
                  />
                </VtkRenderWindowParent>
                <!-- Sibling of the workspace so the overlay paints *before*
                     the workspace's heavy vtk setup runs (the toolbar
                     defers `curvedMPRWorkspaceActive=true` by one rAF, so
                     during that frame this overlay is the only thing
                     mounting). -->
                <component
                  :is="implantPlanningWorkspace?.bootOverlayComponent"
                  v-if="hasData && curvedMPRBooting && implantPlanningWorkspace"
                />
                <welcome-page
                  v-if="!hasData"
                  :loading="showLoading"
                  class="clickable"
                  @click="loadUserPromptedFiles"
                >
                </welcome-page>
              </div>
              <pycad-right-panel />
            </div>
            <pycad-status-bar />
          </div>
        </v-main>
        <controls-modal />
        <nifti-import-dialog />
        <replace-scan-dialog />
        <message-notifications />
      </v-app>
      <persistent-overlay
        :disabled="!dragHover"
        color="#000"
        :opacity="0.3"
        :z-index="2000"
        class="text-center"
      >
        <div class="d-flex flex-column align-center justify-center h-100">
          <div class="dnd-prompt">
            <v-icon size="4.75rem">mdi-download</v-icon>
            <div class="text-h2 font-weight-bold">Drop your files to open</div>
          </div>
        </div>
      </persistent-overlay>
    </template>
  </drag-and-drop>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, watch, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import { UrlParams } from '@vueuse/core';
import vtkURLExtract from '@kitware/vtk.js/Common/Core/URLExtract';
import { useTheme } from 'vuetify';
import useLoadDataStore from '@/src/store/load-data';
import { useViewStore } from '@/src/store/views';
import useRemoteSaveStateStore from '@/src/store/remote-save-state';
import PycadTopToolbar from '@/src/components/pycad/TopToolbar.vue';
import PycadRightPanel from '@/src/components/pycad/RightPanel.vue';
import PycadStatusBar from '@/src/components/pycad/StatusBar.vue';
import {
  loadFiles,
  loadUserPromptedFiles,
  loadUrls,
} from '@/src/actions/loadUserFiles';
import WelcomePage from '@/src/components/WelcomePage.vue';
import { useDICOMStore } from '@/src/store/datasets-dicom';
import LayoutGrid from '@/src/components/LayoutGrid.vue';
import DragAndDrop from '@/src/components/DragAndDrop.vue';
import PersistentOverlay from '@/src/components/PersistentOverlay.vue';
import ControlsModal from '@/src/components/ControlsModal.vue';
import NiftiImportDialog from '@/src/components/NiftiImportDialog.vue';
import ReplaceScanDialog from '@/src/components/ReplaceScanDialog.vue';
import MessageNotifications from '@/src/components/MessageNotifications.vue';
import { useImageStore } from '@/src/store/datasets-images';
import { useModelStore } from '@/src/store/datasets-models';
import { useServerStore } from '@/src/store/server';
import { useGlobalErrorHook } from '@/src/composables/useGlobalErrorHook';
import { useKeyboardShortcuts } from '@/src/composables/useKeyboardShortcuts';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import {
  populateAuthorizationToken,
  stripTokenFromUrl,
} from '@/src/utils/token';
import { defaultImageMetadata } from '@/src/core/progressiveImage';
import VtkRenderWindowParent from '@/src/components/vtk/VtkRenderWindowParent.vue';
import { enabledWorkspaces } from '@/src/features/registry';
import { useSyncWindowing } from '@/src/composables/useSyncWindowing';
import { normalizeUrlParams } from '@/src/utils/urlParams';
import { PycadDarkTheme } from '@/src/constants';
import { usePycadUiStore } from '@/src/store/pycad-ui';
import { getDocumentTitlePrefix } from '@/src/utils/dicomDisplay';

export default defineComponent({
  name: 'App',

  components: {
    LayoutGrid,
    DragAndDrop,
    PersistentOverlay,
    ControlsModal,
    NiftiImportDialog,
    ReplaceScanDialog,
    MessageNotifications,
    WelcomePage,
    PycadTopToolbar,
    PycadRightPanel,
    PycadStatusBar,
    VtkRenderWindowParent,
  },

  setup() {
    const imageStore = useImageStore();
    const dicomStore = useDICOMStore();

    useGlobalErrorHook();
    useKeyboardShortcuts();

    // --- sync handling --- //

    useSyncWindowing();

    // --- file handling --- //

    const loadDataStore = useLoadDataStore();
    const modelStore = useModelStore();
    const hasData = computed(
      () =>
        imageStore.idList.length > 0 ||
        Object.keys(dicomStore.volumeInfo).length > 0 ||
        modelStore.idList.length > 0
    );
    const showLoading = computed(
      () => loadDataStore.isLoading || hasData.value
    );

    const { currentImageMetadata, isImageLoading } = useCurrentImage();
    const defaultImageMetadataName = defaultImageMetadata().name;
    watch(currentImageMetadata, (newMetadata) => {
      let prefix = '';
      if (
        newMetadata?.name &&
        (newMetadata.name !== defaultImageMetadataName || !isImageLoading)
      ) {
        prefix = getDocumentTitlePrefix(newMetadata.name);
      }
      document.title = `${prefix}SmileSuite Dental DICOM Viewer`;
    });

    // --- parse URL -- //

    populateAuthorizationToken();
    stripTokenFromUrl();

    let urlParams: ReturnType<typeof normalizeUrlParams>;
    try {
      urlParams = normalizeUrlParams(
        vtkURLExtract.extractURLParameters() as UrlParams
      );
    } catch (error) {
      console.error('Failed to parse URL parameters:', error);
      urlParams = {};
    }

    onMounted(() => {
      loadUrls(urlParams);
    });

    // --- remote save state URL --- //

    if (import.meta.env.VITE_ENABLE_REMOTE_SAVE && urlParams.save) {
      const url = Array.isArray(urlParams.save)
        ? urlParams.save[0]
        : urlParams.save;
      useRemoteSaveStateStore().setSaveUrl(url);
    }

    // --- remote server --- //

    const serverStore = useServerStore();
    onMounted(() => {
      serverStore.connect();
    });

    // --- layout --- //

    const { visibleLayout } = storeToRefs(useViewStore());
    const { curvedMPRWorkspaceActive, curvedMPRBooting } =
      storeToRefs(usePycadUiStore());

    const implantPlanningWorkspace = enabledWorkspaces().find(
      (ws) => ws.id === 'implantPlanning'
    );

    // --- theme body class sync --- //
    // Some pycad styles (scrollbar, body font) hang off body.pycad-dark.
    const theme = useTheme();
    watchEffect(() => {
      const isPycad = theme.global.name.value === PycadDarkTheme;
      document.body.classList.toggle('pycad-dark', isPycad);
    });

    return {
      loadUserPromptedFiles,
      loadFiles,
      hasData,
      showLoading,
      layout: visibleLayout,
      curvedMPRWorkspaceActive,
      curvedMPRBooting,
      implantPlanningWorkspace,
    };
  },
});
</script>

<style>
#content-main {
  transition: initial;
  width: 100%;
  height: 100%;
  position: fixed;
  padding: 0 !important;
}

.alert > .v-snack__wrapper {
  transition: background-color 0.25s;
}
</style>

<style src="@/src/components/styles/utils.css"></style>

<style scoped>
#app-container {
  width: 100%;
  height: 100%;
}

.pycad-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--pycad-bg);
  overflow: hidden;
}

.pycad-main-row {
  flex: 1 1 auto;
  display: flex;
  flex-direction: row;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.pycad-viewport-column {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--pycad-bg);
  padding: 8px;
}

.dnd-prompt {
  background: rgba(0, 0, 0, 0.4);
  color: white;
  border-radius: 8px;
  box-shadow: 0px 0px 10px 5px rgba(0, 0, 0, 0.4);
  padding: 64px;
}
</style>
